"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import {
  Coordinates,
  calculateDistance as calculateHaversineDistance,
  getLocationCoordinates,
  getCoordinatesForLocation,
} from "@/lib/locationData";
import { reverseGeocode } from "@/lib/googleMapsUtils";
import { useSystemSettings } from "@/contexts/SystemSettingsContext";
import { isGoogleMapsError } from "@/types/googleMapsErrors";
import { enhancedDistanceCalculator } from "@/lib/enhancedDistanceCalculator";
import { geocodeCache } from "@/lib/geocodeCache";
import logger from "@/lib/utils/logger";

// Get coordinates map for Lagos locations (fallback)
const LAGOS_LOCATIONS = getLocationCoordinates();

export type UserLocation = {
  placeId?: string; // Google Maps place ID
  areaName: string;
  formattedAddress?: string; // Full formatted address from Google Maps
  coordinates: Coordinates;
  isGoogleMapsLocation?: boolean; // Flag to indicate if this is from Google Maps
  lastUpdated?: string; // Timestamp for cache validation
};

// Default search radius in kilometers
export const DEFAULT_RADIUS = 5;

type LocationContextType = {
  userLocation: UserLocation | null;
  setUserLocation: (location: UserLocation) => void;
  searchRadius: number;
  setSearchRadius: (radius: number) => void;
  isLocationDetected: boolean;
  isLoadingLocation: boolean;
  calculateDistance: (coords1: Coordinates, coords2: Coordinates) => number;
  calculateDistanceAsync: (
    coords1: Coordinates,
    coords2: Coordinates,
    mode?: "driving" | "walking" | "bicycling" | "transit",
  ) => Promise<number>;
  calculateDistancesAsync: (
    origin: Coordinates,
    destinations: Coordinates[],
    mode?: "driving" | "walking" | "bicycling" | "transit",
  ) => Promise<number[]>;
  updateLocationFromProfile: (profileLocation: string | undefined) => void;
  updateLocationFromGoogleMaps: (location: UserLocation) => void;
  clearLocationCache: () => void;
  geocodeLocationString: (
    locationString: string,
  ) => Promise<UserLocation | null>;
  clearDistanceCache: () => void;
  getDistanceCacheStats: () => {
    totalEntries: number;
    googleEntries: number;
    haversineEntries: number;
    isGoogleMapsAvailable: boolean | null;
    lastGoogleMapsCheck: number;
  };
};

const LocationContext = createContext<LocationContextType | undefined>(
  undefined,
);

export const LocationProvider = ({ children }: { children: ReactNode }) => {
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [searchRadius, setSearchRadius] = useState<number>(DEFAULT_RADIUS);
  const [isLocationDetected, setIsLocationDetected] = useState<boolean>(false);
  const [isLoadingLocation, setIsLoadingLocation] = useState<boolean>(true);
  const [isInitializing, setIsInitializing] = useState<boolean>(false);

  // Use system settings context for Google Maps availability
  const { googleMapsEnabled } = useSystemSettings();

  // Enhanced distance calculation functions
  const calculateDistanceAsync = async (
    coords1: Coordinates,
    coords2: Coordinates,
    mode: "driving" | "walking" | "bicycling" | "transit" = "driving",
  ): Promise<number> => {
    try {
      return await enhancedDistanceCalculator.calculateDistance(
        coords1,
        coords2,
        mode,
        !googleMapsEnabled,
      );
    } catch (error) {
      console.warn(
        "[LOCATION] Enhanced distance calculation failed, using fallback:",
        error,
      );
      return calculateHaversineDistance(coords1, coords2);
    }
  };

  const calculateDistancesAsync = async (
    origin: Coordinates,
    destinations: Coordinates[],
    mode: "driving" | "walking" | "bicycling" | "transit" = "driving",
  ): Promise<number[]> => {
    try {
      // Don't split into chunks - let the batcher handle batching
      // The batcher will automatically split into chunks of 25 when making API calls
      // This allows multiple components to batch their requests together
      return await enhancedDistanceCalculator.calculateDistances(
        origin,
        destinations,
        mode,
        !googleMapsEnabled,
      );
    } catch (error) {
      console.warn(
        "[LOCATION] Enhanced batch distance calculation failed, using fallback:",
        error,
      );
      return destinations.map((dest) =>
        calculateHaversineDistance(origin, dest),
      );
    }
  };

  const clearDistanceCache = () => {
    enhancedDistanceCalculator.clearCache();
  };

  const getDistanceCacheStats = () => {
    return enhancedDistanceCalculator.getCacheStats();
  };

  // Helper function to clear location cache
  const clearLocationCache = () => {
    localStorage.removeItem("userLocation");
  };

  // Helper function to set default location
  const setDefaultLocation = () => {
    logger.log("[LOCATION] Setting default location to Ikeja");
    const defaultLocation: UserLocation = {
      areaName: "Ikeja",
      coordinates: LAGOS_LOCATIONS["Ikeja"],
      formattedAddress: "Ikeja, Lagos, Nigeria",
      isGoogleMapsLocation: false,
    };

    setUserLocation(defaultLocation);
    setIsLocationDetected(true);
    setIsLoadingLocation(false);
    // Save default location to localStorage
    localStorage.setItem("userLocation", JSON.stringify(defaultLocation));
  };

  // Function to geocode a location string using Google Maps with enhanced error handling
  const geocodeLocationString = useCallback(
    async (locationString: string): Promise<UserLocation | null> => {
      logger.log("[LOCATION] Geocoding location string:", {
        locationString,
        googleMapsEnabled,
      });

      // Only try Google Maps geocoding if it's enabled
      if (!googleMapsEnabled) {
        logger.log("[LOCATION] Google Maps disabled, using local fallback");

        // Try to find local coordinates using our local data
        const localCoordinates = LAGOS_LOCATIONS[locationString];
        if (localCoordinates) {
          logger.log("[LOCATION] Found local coordinates:", localCoordinates);
          return {
            areaName: locationString,
            coordinates: localCoordinates,
            isGoogleMapsLocation: false,
          };
        }

        // Fallback to default location if not found locally
        logger.log(
          "[LOCATION] No local coordinates found, using default location",
        );
        return null;
      }

      try {
        logger.log(
          "[LOCATION] Attempting Google Maps geocoding for:",
          locationString,
        );

        // Use cached geocode which returns both coordinates and formattedAddress
        const cached = await geocodeCache.geocode(locationString);
        if (cached && cached.coordinates) {
          // Use formattedAddress from cache if available, otherwise try reverse geocode
          let formattedAddress = cached.formattedAddress;

          // Only do reverse geocode if we don't have formattedAddress
          if (!formattedAddress) {
            formattedAddress =
              (await reverseGeocode(cached.coordinates)) || locationString;
          }

          const result = {
            areaName: locationString.split(",")[0].trim(),
            coordinates: cached.coordinates,
            formattedAddress: formattedAddress,
            isGoogleMapsLocation: true,
          };
          logger.log("[LOCATION] Successfully geocoded:", result);
          return result;
        }
      } catch (error) {
        if (isGoogleMapsError(error)) {
          logger.log(
            `[LOCATION] Google Maps geocoding failed (${error.type}):`,
            error.message,
          );
          if (error.type === "DISABLED") {
            logger.log(
              "[LOCATION] Google Maps is disabled, falling back to local data",
            );
          }
        } else {
          console.error("[LOCATION] Unexpected error during geocoding:", error);
        }
      }
      return null;
    },
    [googleMapsEnabled],
  );

  // Function to update location from Google Maps data
  const updateLocationFromGoogleMaps = (location: UserLocation) => {
    console.log("[LOCATION] Updating from Google Maps:", location);
    setUserLocation(location);
    setIsLocationDetected(true);
    setIsLoadingLocation(false);
    // Save location to localStorage
    localStorage.setItem("userLocation", JSON.stringify(location));
  };

  // Function to update location from profile data with enhanced fallback logic
  const updateLocationFromProfile = useCallback(
    async (profileLocation: string | undefined) => {
      logger.log("[LOCATION] Updating from profile:", {
        profileLocation,
        currentLocation: userLocation?.areaName,
        isLocationDetected: isLocationDetected,
        googleMapsEnabled: googleMapsEnabled,
      });

      if (!profileLocation) {
        logger.log("[LOCATION] No profile location provided, setting default");
        setDefaultLocation();
        return;
      }

      // Prevent unnecessary updates if location hasn't changed
      // Also check if the location string matches (handles different formats)
      const currentLocationName = userLocation?.areaName?.toLowerCase().trim();
      const profileLocationName = profileLocation.toLowerCase().trim();

      // Check if they match exactly, or if current location is the first part of profile location
      if (
        currentLocationName === profileLocationName ||
        (currentLocationName &&
          profileLocationName.startsWith(currentLocationName)) ||
        (currentLocationName &&
          currentLocationName === profileLocationName.split(",")[0])
      ) {
        logger.log("[LOCATION] Location unchanged, skipping update");
        return;
      }

      // Only try Google Maps geocoding if it's enabled
      if (googleMapsEnabled) {
        logger.log("[LOCATION] Google Maps enabled, trying geocoding first");

        try {
          const geocodedLocation = await geocodeLocationString(profileLocation);

          if (geocodedLocation) {
            logger.log(
              "[LOCATION] Successfully geocoded profile location:",
              geocodedLocation,
            );
            // Update state and cache atomically
            const newLocation = {
              ...geocodedLocation,
              lastUpdated: new Date().toISOString(), // Add timestamp for cache validation
            };
            setUserLocation(newLocation);
            setIsLocationDetected(true);
            localStorage.setItem("userLocation", JSON.stringify(newLocation));
            return;
          }
        } catch (error) {
          console.warn(
            "[LOCATION] Geocoding failed, falling back to local data:",
            error,
          );
        }
      }

      // Fallback to local location data using proper lookup function
      logger.log(
        "[LOCATION] Attempting local location lookup for:",
        profileLocation,
      );

      const coords = getCoordinatesForLocation(profileLocation);

      if (coords) {
        // Parse location string to extract area name
        const parts = profileLocation.split(",");
        const locationKey = parts[0].trim();

        // Determine state from location string or default to detected state
        // Note: State detection is kept for potential future use
        const _state = profileLocation.toLowerCase().includes("lagos")
          ? "Lagos"
          : profileLocation.toLowerCase().includes("oyo")
            ? "Oyo"
            : "Nigeria";

        // Update state and cache atomically
        const newLocation: UserLocation = {
          areaName: locationKey,
          coordinates: coords,
          formattedAddress: profileLocation,
          isGoogleMapsLocation: false,
          lastUpdated: new Date().toISOString(), // Add timestamp for cache validation
        };
        logger.log("[LOCATION] Found matching local location:", newLocation);
        setUserLocation(newLocation);
        setIsLocationDetected(true);
        setIsLoadingLocation(false);
        localStorage.setItem("userLocation", JSON.stringify(newLocation));
        return;
      }

      logger.log("[LOCATION] No matching location found, setting default");
      setDefaultLocation();
    },
    [
      userLocation,
      googleMapsEnabled,
      geocodeLocationString,
      isLocationDetected,
      setUserLocation,
      setIsLocationDetected,
      setIsLoadingLocation,
    ],
  );

  // Modified mount effect to check localStorage first
  useEffect(() => {
    // Prevent multiple simultaneous initializations
    if (isInitializing) {
      return;
    }

    const initTimer = setTimeout(() => {
      setIsInitializing(true);
    }, 0);

    try {
      // Try to get location from localStorage first
      const savedLocation = localStorage.getItem("userLocation");
      if (savedLocation) {
        const parsedLocation = JSON.parse(savedLocation);

        // Basic validation - must have required fields
        if (parsedLocation.areaName && parsedLocation.coordinates) {
          logger.log("[LOCATION] Using cached location:", parsedLocation);
          const timer = setTimeout(() => {
            setUserLocation(parsedLocation);
            setIsLocationDetected(true);
            setIsLoadingLocation(false);
            setIsInitializing(false);
          }, 0);

          // Check cache age in background without blocking UI
          const now = new Date().getTime();
          const lastUpdated = parsedLocation.lastUpdated
            ? new Date(parsedLocation.lastUpdated).getTime()
            : 0;
          const cacheAge = now - lastUpdated;
          const CACHE_VALIDITY = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

          if (cacheAge > CACHE_VALIDITY) {
            logger.log("[LOCATION] Cache expired, refreshing in background");
            // Refresh location in background if cache is old
            if (parsedLocation.areaName) {
              const refreshTimer = setTimeout(() => {
                updateLocationFromProfile(parsedLocation.areaName).catch(
                  (error) => {
                    console.error(
                      "[LOCATION] Background refresh failed:",
                      error,
                    );
                  },
                );
              }, 0);
              return () => {
                clearTimeout(timer);
                clearTimeout(initTimer);
                clearTimeout(refreshTimer);
              };
            }
          }
          return () => {
            clearTimeout(timer);
            clearTimeout(initTimer);
          };
        } else {
          logger.log("[LOCATION] Cached location invalid, setting default");
          const timer = setTimeout(() => {
            setDefaultLocation();
            setIsInitializing(false);
          }, 0);
          return () => {
            clearTimeout(timer);
            clearTimeout(initTimer);
          };
        }
      } else if (!userLocation) {
        logger.log("[LOCATION] No saved location found, setting default");
        const timer = setTimeout(() => {
          setDefaultLocation();
          setIsInitializing(false);
        }, 0);
        return () => {
          clearTimeout(timer);
          clearTimeout(initTimer);
        };
      }
    } catch (error) {
      console.error(
        "[LOCATION] Error restoring location from localStorage:",
        error,
      );
      const timer = setTimeout(() => {
        setDefaultLocation();
        setIsInitializing(false);
      }, 0);
      return () => {
        clearTimeout(timer);
        clearTimeout(initTimer);
      };
    }
    return () => clearTimeout(initTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - this should only run on mount

  // Create a context value object
  const contextValue = {
    userLocation,
    setUserLocation: (location: UserLocation) => {
      setUserLocation(location);
      // Save to localStorage whenever location is updated
      localStorage.setItem("userLocation", JSON.stringify(location));
    },
    searchRadius,
    setSearchRadius,
    isLocationDetected,
    isLoadingLocation,
    calculateDistance: calculateHaversineDistance, // Keep synchronous version for backward compatibility
    calculateDistanceAsync,
    calculateDistancesAsync,
    updateLocationFromProfile,
    updateLocationFromGoogleMaps,
    clearLocationCache,
    geocodeLocationString,
    clearDistanceCache,
    getDistanceCacheStats,
  };

  return (
    <LocationContext.Provider value={contextValue}>
      {children}
    </LocationContext.Provider>
  );
};

// Custom hook to use the location context
export const useLocation = () => {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error("useLocation must be used within a LocationProvider");
  }
  return context;
};

export default LocationContext;
